import React, { useEffect, useState, useMemo } from 'react'
import { Collapsible } from 'grommet'
import PropTypes from 'prop-types'

import {
  AccordionContent,
  AccordionContentWrapper,
  AccordionPanel,
  AccordionStatus,
  AccordionTitleWrapper,
  AccordionTitle,
  AccordionWrapper,
  StyledCaret,
} from './component.styles'

const callAllOnClicks = (...fns) => (...args) =>
  fns.forEach(fn => fn && fn(...args))

const Accordion = ({
  accordionPanelDataTestId,
  ariaLabel,
  children,
  className,
  contentPadding,
  dataTestId,
  followActiveProp,
  fontColor,
  iconColor,
  iconHoverColor,
  isActive = false,
  onClick,
  panelColor,
  status,
  title,
  titleAs,
}) => {
  const [active, setActive] = useState(isActive)
  const [isKeyboardFocused, setIsKeyboardFocused] = useState(false)
  const [isMouseFocused, setIsMouseFocused] = useState(false)
  const uniqueId = useMemo(
    () =>
      `accordion-${Math.random()
        .toString(36)
        .substring(2, 9)}`,
    [],
  )
  const toggleAccordion = () => {
    setActive(prevState => {
      return !prevState
    })
  }
  const label = typeof title === 'string' ? title : ariaLabel
  const wrapperClasses = isKeyboardFocused
    ? `${className} keyboard-focus`
    : className
  const caretClasses = isKeyboardFocused
    ? active
      ? 'rotate keyboard-focus'
      : 'keyboard-focus'
    : active
    ? 'rotate'
    : ''

  useEffect(() => {
    if (followActiveProp) setActive(isActive)
  }, [isActive])

  const setFocus = () => {
    if (isMouseFocused) return
    setIsKeyboardFocused(true)
  }
  const setBlur = () => {
    setIsKeyboardFocused(false)
    setIsMouseFocused(false)
  }
  const setKeyDown = () => {
    setIsMouseFocused(false)
    setIsKeyboardFocused(true)
  }
  const setMouseDown = () => {
    setIsMouseFocused(true)
    setIsKeyboardFocused(false)
  }

  return (
    <AccordionWrapper
      className={wrapperClasses}
      dataTestId={dataTestId}
      panelColor={panelColor}
    >
      <AccordionPanel
        aria-controls={`accordion-content-${uniqueId}`}
        aria-expanded={active}
        aria-label={label}
        dataTestId={accordionPanelDataTestId}
        onBlur={setBlur}
        onClick={callAllOnClicks(toggleAccordion, onClick)}
        onFocus={setFocus}
        onKeyDown={setKeyDown}
        onMouseDown={setMouseDown}
        panelColor={panelColor}
        role="button"
      >
        <AccordionTitleWrapper>
          {/* iconcolor must be passed lowercase to avoid React warning */}
          <StyledCaret
            className={caretClasses}
            iconHoverColor={iconHoverColor}
            iconcolor={iconColor}
          />
          <AccordionTitle as={titleAs} fontColor={fontColor}>
            {title}
          </AccordionTitle>
        </AccordionTitleWrapper>
        {status && <AccordionStatus>{status}</AccordionStatus>}
      </AccordionPanel>
      <AccordionContentWrapper id={`accordion-content-${uniqueId}`}>
        <Collapsible open={active}>
          <AccordionContent contentPadding={contentPadding}>
            {children}
          </AccordionContent>
        </Collapsible>
      </AccordionContentWrapper>
    </AccordionWrapper>
  )
}
Accordion.propTypes = {
  accordionPanelDataTestId: PropTypes.string,
  ariaLabel: PropTypes.string,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  contentPadding: PropTypes.string,
  dataTestId: PropTypes.string,
  followActiveProp: PropTypes.bool,
  fontColor: PropTypes.string,
  iconColor: PropTypes.string,
  iconHoverColor: PropTypes.string,
  isActive: PropTypes.bool,
  onClick: PropTypes.func,
  panelColor: PropTypes.string,
  status: PropTypes.node,
  title: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  titleAs: PropTypes.string,
}

export default Accordion
